import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuditLoggers } from '../../../utils/auditLogger';
import {
  Box,
  Typography,
  Paper,
  Tooltip,
  Grid,
  Button,
  Card,
  CardContent,
  Tabs,
  Tab,
  TextField,
  Container,
  IconButton,
  Chip,
  List,
  ListItem
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import MenuIcon from '@mui/icons-material/Menu';
import UploadIcon from '@mui/icons-material/Upload';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ListAltIcon from '@mui/icons-material/ListAlt';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import CardDetailsModal from './CardDetailsModal';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateKpiCardSx,
  harmonateDataGridSx,
  harmonateProceedButtonSx,
  harmonateTabsSx,
  harmonateGridActionIconSx,
  harmonateFormFieldsSx,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

// Import the details page
import Details from './Details';

// Mock data for charts
const lineChartData = [
  { month: 'Jul', value1: 120, value2: 100 },
  { month: 'Aug', value1: 140, value2: 110 },
  { month: 'Sep', value1: 130, value2: 105 },
  { month: 'Oct', value1: 150, value2: 115 },
  { month: 'Nov', value1: 160, value2: 120 },
  { month: 'Dec', value1: 140, value2: 110 },
];

const disputesByPropertyData = [
  { name: 'A', value: 410 },
  { name: 'B', value: 360 },
  { name: 'C', value: 310 },
  { name: 'D', value: 250 },
  { name: 'E', value: 100 },
];

const disputeRatioData = [
  { name: 'No Action', value: 85, color: ht.green },
  { name: 'Disputed', value: 15, color: ht.text },
];

const dailyWinsLossesData = [
  { day: 'Sun', wins: 5, losses: 1 },
  { day: 'Mon', wins: 4, losses: 2 },
  { day: 'Tue', wins: 3, losses: 1 },
  { day: 'Wed', wins: 4, losses: 2 },
  { day: 'Thu', wins: 3, losses: 1 },
  { day: 'Fri', wins: 2, losses: 1 },
  { day: 'Sat', wins: 1, losses: 0 },
];

// Mock data for tables
const recentsData = [
  {
    id: 1,
    caseNumber: '89357382',
    deadline: '2023-5-11',
    mid: '234903921',
    alias: 'BofA $500K US-Subscription',
    cardNumber: '5780*******1344',
    brand: 'Mastercard',
  },
  {
    id: 2,
    caseNumber: '64728567',
    deadline: '2023-3-22',
    mid: '7392015478',
    alias: 'BofA $500K UK-Standard',
    cardNumber: '46353*******5643',
    brand: 'Visa',
  },
  {
    id: 3,
    caseNumber: '21236784',
    deadline: '2023-2-11',
    mid: '9821346273',
    alias: 'Chase $250K US-StraightSale',
    cardNumber: '46456*******4354',
    brand: 'Visa',
  },
  {
    id: 4,
    caseNumber: '65735637',
    deadline: '2023-1-25',
    mid: '0328427819',
    alias: 'WellsFargo $500K US-Subscription',
    cardNumber: '48239*******2234',
    brand: 'Visa',
  },
];

const workflowsData = [
  ...recentsData,
  {
    id: 5,
    caseNumber: '74838567',
    deadline: '2023-1-20',
    mid: '8493027564',
    alias: 'WellsFargo $250K US-Subscription',
    cardNumber: '84392*******4489',
    brand: 'Mastercard',
  },
  {
    id: 6,
    caseNumber: '34214738',
    deadline: '2023-1-20',
    mid: '0283638902',
    alias: 'BofA $500K UK-Standard',
    cardNumber: '94503*******2987',
    brand: 'Mastercard',
  },
  {
    id: 7,
    caseNumber: '39402840',
    deadline: '2023-1-16',
    mid: '4422678490',
    alias: 'Chase $250K US-StraightSale',
    cardNumber: '74329*******3289',
    brand: 'Mastercard',
  },
  {
    id: 8,
    caseNumber: '33479200',
    deadline: '2023-1-8',
    mid: '3839484930',
    alias: 'BofA $500K US-Subscription',
    cardNumber: '65118*******7539',
    brand: 'Mastercard',
  },
];

// Table columns configuration
// ActionMenu component to handle the menu state
const ActionMenu = ({ row, onViewDetails }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event) => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  const handleViewDetails = (event) => {
    event.stopPropagation();
    onViewDetails(row);
    setAnchorEl(null);
  };

  return (
    <div>
      <IconButton
        size="small"
        onClick={handleClick}
        aria-label="actions"
        aria-controls={open ? 'action-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="action-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleViewDetails}>
          <ListItemIcon>
            <ListAltIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
      </Menu>
    </div>
  );
};

const createTableColumns = (onViewDetails) => [
  {
    field: 'actions',
    headerName: 'Actions',
    width: 100,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
      <ActionMenu row={params.row} onViewDetails={onViewDetails} />
    ),
  },
  { field: 'caseNumber', headerName: 'Case Number', width: 150 },
  { field: 'deadline', headerName: 'Recommended Deadline', width: 200 },
  { field: 'mid', headerName: 'MID', width: 150 },
  { field: 'alias', headerName: 'MID Alias', width: 300 },
  { field: 'cardNumber', headerName: 'Card Number', width: 200 },
  { field: 'brand', headerName: 'Card Brand', width: 150 },
];

// Insights Component
const Insights = ({ onShowWorkflows, onRowClick }) => {
  const [selectedQuarter, setSelectedQuarter] = useState('Q2');

  // State for the insights menu
  const [insightsAnchorEl, setInsightsAnchorEl] = React.useState(null);
  const isInsightsMenuOpen = Boolean(insightsAnchorEl);

  const handleInsightsMenuClick = (event) => {
    event.stopPropagation();
    setInsightsAnchorEl(event.currentTarget);
  };

  const handleInsightsMenuClose = (event) => {
    event?.stopPropagation();
    setInsightsAnchorEl(null);
  };

  const handleViewAllWorkflows = (event) => {
    event.stopPropagation();
    const defaultRow = { id: 'default-charge' }; 
    onShowWorkflows(defaultRow);
    setInsightsAnchorEl(null);
  };

  return (
    <Box sx={{ ...harmonatePageSx, height: '100%', minHeight: '100vh', py: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, px: 1 }}>
        <Typography variant="h6" sx={harmonatePageTitleSx}>
          Insights
        </Typography>
        <IconButton
          size="small"
          onClick={handleInsightsMenuClick}
          aria-label="insights actions"
          aria-controls={isInsightsMenuOpen ? 'insights-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={isInsightsMenuOpen ? 'true' : undefined}
          sx={{ color: ht.white, '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.12)' } }}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          id="insights-menu"
          anchorEl={insightsAnchorEl}
          open={isInsightsMenuOpen}
          onClose={handleInsightsMenuClose}
          onClick={handleInsightsMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          slotProps={{
            paper: {
              sx: {
                borderRadius: ht.fieldRadius,
                '& .MuiMenuItem-root': { fontSize: '0.875rem', color: ht.text },
              },
            },
          }}
        >
          <MenuItem onClick={handleViewAllWorkflows}>
            <ListItemIcon>
              <ListAltIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View All Chargebacks</ListItemText>
          </MenuItem>
        </Menu>
      </Box>
    
      {/* Charts Section */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {/* Total Count Card with Line Chart */}
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ ...harmonateKpiCardSx, height: 180 }}>
            <CardContent sx={{ height: '100%', display: 'flex' }}>
              {/* Left side - Stats */}
              <Box sx={{ flex: '0 0 130px', display: 'flex', flexDirection: 'column', justifyContent: 'center', pr: 1 }}>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', mb: 0.5, fontSize: '0.7rem' }}>
                  📊 This month
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, fontSize: '0.75rem' }}>
                  Total Count
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                  1,440
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', fontSize: '0.7rem' }}>
                    <span style={{ color: ht.text, marginRight: '4px' }}>—</span>
                    2024
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', fontSize: '0.7rem' }}>
                    <span style={{ color: ht.green, marginRight: '4px' }}>—</span>
                    2025
                  </Typography>
                </Box>
              </Box>
              
              {/* Right side - Line Chart */}
              <Box sx={{ flex: 1, height: '100%', minWidth: 0, ml: 1, mt: 1 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={lineChartData}>
                    <XAxis dataKey="month" fontSize={8} />
                    <Line type="monotone" dataKey="value1" stroke={ht.green} strokeWidth={1.5} dot={{ fill: ht.green, strokeWidth: 1, r: 3 }} />
                    <Line type="monotone" dataKey="value2" stroke={ht.blue} strokeWidth={1.5} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Disputes by Property */}
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ ...harmonateKpiCardSx, height: 180, width: '100%' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', fontSize: '0.8rem' }}>
                Disputes by Property
              </Typography>
              <ResponsiveContainer width="100%" height={120}>
                <BarChart data={disputesByPropertyData}>
                  <XAxis dataKey="name" fontSize={8} />
                  <Bar dataKey="value" fill={ht.green} radius={[1, 1, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Dispute Ratio */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ ...harmonateKpiCardSx, height: 180, width: '100%' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 500, fontSize: '0.85rem',fontWeight: 'bold', }}>
                Dispute Ratio
              </Typography>
              <Box sx={{ position: 'relative', height: 90, display: 'flex', justifyContent: 'center' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={disputeRatioData}
                      cx="50%"
                      cy="50%"
                      innerRadius={25}
                      outerRadius={45}
                      dataKey="value"
                    >
                      {disputeRatioData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 8, height: 8, bgcolor: ht.green, borderRadius: '50%', mr: 0.5 }} />
                  <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>No Action 85%</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 8, height: 8, bgcolor: ht.blue, borderRadius: '50%', mr: 0.5 }} />
                  <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Disputed 15%</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Daily Wins to Losses */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ ...harmonateKpiCardSx, height: 180, width: '100%' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 500, fontSize: '0.85rem',fontWeight: 'bold', }}>
                Daily Wins to Losses
              </Typography>
              <ResponsiveContainer width="100%" height={90}>
                <BarChart data={dailyWinsLossesData}>
                  <XAxis dataKey="day" fontSize={8} />
                  <Bar dataKey="wins" fill={ht.green} radius={[1, 1, 0, 0]} />
                  <Bar dataKey="losses" fill="#f44336" radius={[1, 1, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 8, height: 8, bgcolor: ht.green, borderRadius: '50%', mr: 0.5 }} />
                  <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Won</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 8, height: 8, bgcolor: '#f44336', borderRadius: '50%', mr: 0.5 }} />
                  <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Lost</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Insights Tiles Section */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {/* Total Count by Month Chart */}
        <Grid item xs={12} sm={6} md={4} lg>
          <Card sx={{ height: '100%', ...harmonateKpiCardSx }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Total Count by Month
              </Typography>
              <ResponsiveContainer width="100%" height={150}>
                <LineChart data={lineChartData}>
                  <XAxis dataKey="month" fontSize={10} />
                  <YAxis />
                  <Line type="monotone" dataKey="value1" stroke={ht.green} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Chargeback Success Rate */}
        <Grid item xs={12} sm={6} md={4} lg>
          <Card sx={{ height: '100%', ...harmonateKpiCardSx }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Chargeback Success Rate
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 120 }}>
                <Typography variant="h4" sx={{ color: ht.green, fontWeight: 'bold', mb: 1 }}>
                  78%
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ textAlign: 'center' }}>
                  Success rate for disputed chargebacks
                </Typography>
                <Box sx={{ width: '100%', height: 8, bgcolor: '#e0e0e0', borderRadius: 4, mt: 2, overflow: 'hidden' }}>
                  <Box sx={{ width: '78%', height: '100%', bgcolor: ht.green }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Recent Chargeback Activity */}
        <Grid item xs={12} sm={6} md={4} lg>
          <Card sx={{ height: '100%', ...harmonateKpiCardSx }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Recent Chargeback Activity
              </Typography>
              <List dense sx={{ maxHeight: 150, overflow: 'auto' }}>
                {recentsData.slice(0, 4).map((item, index) => (
                  <ListItem 
                    key={`recent-${index}`} 
                    disablePadding 
                    sx={{ 
                      '&:not(:last-child)': { 
                        borderBottom: '1px solid', 
                        borderColor: 'divider',
                        mb: 0.5 
                      } 
                    }}
                  >
                    <ListItemText 
                      primary={`Case #${item.caseNumber}`} 
                      primaryTypographyProps={{ variant: 'caption', fontWeight: 'medium' }}
                      secondary={`${item.alias} - ${item.brand}`}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                    <Chip 
                      label={item.deadline} 
                      size="small" 
                      sx={{ 
                        fontSize: '0.6rem', 
                        height: '20px',
                        bgcolor: index % 2 === 0 ? ht.secondaryTab : 'rgba(46, 166, 160, 0.12)',
                        color: index % 2 === 0 ? ht.text : ht.darkBlue
                      }} 
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Approaching Deadlines */}
        <Grid item xs={12} sm={6} md={4} lg>
          <Card sx={{ height: '100%', ...harmonateKpiCardSx }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  Approaching Deadlines
                </Typography>
                <Chip 
                  label={recentsData.length} 
                  size="small" 
                  color="error" 
                  sx={{ fontWeight: 'bold', minWidth: '24px', height: '20px' }} 
                />
              </Box>
              <List dense sx={{ maxHeight: 150, overflow: 'auto' }}>
                {recentsData.slice(0, 3).map((item, index) => (
                  <ListItem 
                    key={index} 
                    disablePadding 
                    sx={{ 
                      '&:not(:last-child)': { 
                        borderBottom: '1px solid', 
                        borderColor: 'divider',
                        mb: 0.5 
                      } 
                    }}
                  >
                    <ListItemText 
                      primary={`${item.caseNumber} - ${item.alias}`} 
                      primaryTypographyProps={{ variant: 'caption', fontWeight: 'medium' }}
                      secondary={`Due: ${item.deadline}`}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

// Workflows Component
const Workflows = ({ onRowClick, activeTab, currentView, onTabChange }) => {
  const { canUpload } = useFunctionPermissions(FC.MERCHANT_DATA_HUB);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRow, setSelectedRow] = useState(null);
  const [cards] = useState(workflowsData);

  const handleTabChange = (event, newValue) => {
    if (onTabChange) {
      onTabChange(newValue);
    }
  };

  const handleOpenEditModal = (card) => {
    // Edit functionality removed as per requirements
    console.log('Edit functionality has been removed');
  };

  const filteredData = cards.filter(item =>
    item.caseNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.alias?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.mid?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Use createTableColumns for all columns including actions
  const columns = createTableColumns(onRowClick);

  return (
    <Box sx={{ ...harmonatePageSx, height: '100vh' }}>
      <Typography variant="h6" sx={{ ...harmonatePageTitleSx, mb: 2 }}>
        Card Details
      </Typography>
      
      <Paper sx={{ ...harmonateCardSx, bgcolor: ht.white, p: 2.5 }}>
        {/* Tabs */}
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ ...harmonateTabsSx, mb: 2 }}>
          <Tab label="All" sx={{ fontSize: '0.85rem', textTransform: 'none' }} />
          <Tab label="Pending Result" sx={{ fontSize: '0.85rem', textTransform: 'none' }} />
          <Tab label="Completed" sx={{ fontSize: '0.85rem', textTransform: 'none' }} />
        </Tabs>

        <Box sx={{ mb: 2 }}>
          <TextField
            placeholder="Type to search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1, fontSize: '1rem' }} />,
            }}
            sx={{
              width: '300px',
              ...harmonateFormFieldsSx,
              '& .MuiInputBase-input': {
                fontSize: '0.8rem',
                py: 1,
              },
              '& .MuiOutlinedInput-root': {
                height: '36px',
              },
            }}
          />
        </Box>

        {/* Table */}
        <DataGrid
          rows={filteredData}
          columns={columns}
          rowSelectionModel={selectedRow ? [selectedRow.id] : []}
          autoHeight
          hideFooter
          disableRowSelectionOnClick
          onRowClick={(params, event) => {
            // Prevent any row click navigation
            event.stopPropagation();
            // Only allow action button clicks
            if (params.field === 'actions') {
              // Handle action button click if needed
            }
          }}
          sx={{
            ...harmonateDataGridSx,
            '& .MuiDataGrid-row:nth-of-type(2)': {
              backgroundColor: ht.secondaryTab,
            },
            '& .MuiDataGrid-row': {
              cursor: currentView === 'workflows' ? 'pointer' : 'default',
            },
            '& .MuiDataGrid-cell': {
              fontSize: '0.8rem',
            },
            '& .MuiDataGrid-columnHeaders': {
              fontSize: '0.8rem',
            },
          }}
        />
      </Paper>

      {/* Bottom Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<UploadIcon />}
          disabled={!canUpload}
          sx={harmonateProceedButtonSx}
        >
          File Upload
        </Button>
        <Button
          endIcon={<KeyboardArrowDownIcon />}
          sx={{
            color: ht.white,
            fontSize: '0.85rem',
            textTransform: 'none',
          }}
        >
          Q2 2025
        </Button>
      </Box>
    </Box>
  );
};

// Main Chargeback Component
const Chargeback = () => {
  const navigate = useNavigate();
  
  // Log when component mounts (user views all chargebacks)
  useEffect(() => {
    const logViewAllChargebacks = async () => {
      try {
        await AuditLoggers.chargebackViewAll();
      } catch (error) {
        console.error('Failed to log chargeback view:', error);
      }
    };
    
    logViewAllChargebacks();
  }, []);
  
  const handleRowClick = (row) => {
    // Navigate to charge details page when a row is clicked
    navigate(`/chargeback/charge/${row.id}`);
  };

  const handleShowWorkflows = (row) => {
    // Navigate to charge details page with the row's ID
    navigate(`/chargeback/charge/${row.id}`);
  };

  return (
    <Box sx={{ ...harmonatePageSx, height: '100vh' }}>
      <Insights 
        onShowWorkflows={handleShowWorkflows}
        onRowClick={handleRowClick}
      />
    </Box>
  );
};

export default Chargeback;